/* eslint-disable react/no-array-index-key */
import * as React from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import classNames from 'classnames';

type UIImg = { url: string; alt?: string | null };
type Item = { thumbnail: UIImg; image: UIImg };

export type GalleryProps = {
	readonly items: Item[];
	readonly title?: React.ReactNode;

	/** Masonry uniforme (por columnas) */
	readonly columns?: Record<number, number>;

	/** Patrón por filas (p. ej. [3,2,3]). Si lo pasas, se activa el modo patrón */
	readonly pattern?: number[];

	/** Alto fijo por “tile” (recomendado en modo patrón) */
	readonly itemHeight?: number | string;

	/** Espacio entre celdas (px o string) */
	readonly gap?: number | string;

	/** Espacio vertical entre filas (solo patrón) */
	readonly rowGap?: number | string;

	/** Alto fijo del contenedor (viewport con scroll interno) */
	readonly fixedHeight?: number | string;

	/** Overflow-y cuando hay fixedHeight */
	readonly overflow?: 'auto' | 'hidden' | 'scroll';

	/** Radio de borde en px o string (si no, usa el valor del SCSS) */
	readonly radius?: number | string;

	/** Por defecto usa la miniatura en el grid para rendimiento */
	readonly hasThumbnails?: boolean;

	readonly className?: string;
};

const baseClass = 'c-cnm-gallery';

const toPx = (v?: number | string) => (typeof v === 'number' ? `${v}px` : v);

/** Divide el array en grupos siguiendo el patrón (repitiéndolo si hace falta) */
function chunkByPattern<T>(arr: T[], pattern: number[]): T[][] {
	if (!pattern?.length) return [arr];
	const rows: T[][] = [];
	let i = 0;
	let p = 0;
	while (i < arr.length) {
		const take = pattern[p % pattern.length] || arr.length;
		rows.push(arr.slice(i, i + take));
		i += take;
		p += 1;
	}
	return rows;
}

/** Breakpoints por fila: 1 col en móvil, 2 en tablet, 3 en desktop, y en ≥1440px el nº de la fila */
function rowBreakpoints(n: number): Record<number, number> {
	return {
		350: 1,
		768: Math.min(1, n),
		1024: Math.min(2, n),
		1440: n,
	};
}

export const Gallery: React.FC<GalleryProps> = ({
	items,
	title,
	columns = { 350: 1, 768: 2, 1024: 3, 1440: 4 },
	pattern,
	itemHeight,
	gap = 32,
	rowGap = 32,
	fixedHeight,
	overflow = 'auto',
	radius,
	hasThumbnails = true,
	className,
}) => {
	const [open, setOpen] = React.useState(false);
	const [index, setIndex] = React.useState(0);

	const slides = React.useMemo(
		() =>
			items.map((it) => ({
				src: it.image.url,
				alt: it.image.alt ?? '',
			})),
		[items],
	);

	const isPattern = Boolean(pattern?.length);
	const wrapperClassName = React.useMemo(
		() =>
			classNames(
				baseClass,
				{
					[`${baseClass}--fixed`]: Boolean(fixedHeight),
					[`${baseClass}--tile-fixed`]: Boolean(itemHeight),
					[`${baseClass}--pattern`]: isPattern,
				},
				className,
			),
		[className, fixedHeight, itemHeight, isPattern],
	);

	const styleVars: React.CSSProperties = {
		...(fixedHeight ? ({ ['--gallery-height' as any]: toPx(fixedHeight) } satisfies React.CSSProperties) : null),
		...(itemHeight ? ({ ['--gallery-item-height' as any]: toPx(itemHeight) } satisfies React.CSSProperties) : null),
		...(radius ? ({ ['--gallery-radius' as any]: toPx(radius) } satisfies React.CSSProperties) : null),
		...(fixedHeight ? { overflowY: overflow } : null),
	};

	const gutter = typeof gap === 'number' ? `${gap}px` : gap;
	const rowGapPx = typeof rowGap === 'number' ? `${rowGap}px` : rowGap;

	// Grid SRC para rendimiento (thumb en grid, full en lightbox)
	const gridSrc = (it: Item) => (hasThumbnails ? it.image.url : it.image.url);
	const gridAlt = (it: Item) => it.thumbnail.alt ?? it.image.alt ?? '';

	if (!items?.length) return null;

	return (
		<section className={wrapperClassName} style={styleVars}>
			{title ? (
				<header className={`${baseClass}__header`}>
					<h2 className={`${baseClass}__title`}>{title}</h2>
				</header>
			) : null}

			{isPattern ? (
				// ====== MODO PATRÓN (por filas) ======
				<div className={`${baseClass}__rows`} style={{ display: 'grid', rowGap: rowGapPx }}>
					{chunkByPattern(items, pattern!).map((row, rIdx, rows) => {
						const offset = rows.slice(0, rIdx).reduce((acc, g) => acc + g.length, 0);
						return (
							<ResponsiveMasonry key={`row-${rIdx}`} columnsCountBreakPoints={rowBreakpoints(row.length)}>
								<Masonry style={{ gap: gutter }} itemStyle={{ gap: gutter }}>
									{row.map((it, i) => (
										<button
											key={`${gridSrc(it)}-${offset + i}`}
											type="button"
											aria-label={gridAlt(it) || 'Ver imagen'}
											className={`${baseClass}__item`}
											style={itemHeight ? { height: toPx(itemHeight) } : undefined}
											onClick={() => {
												setIndex(offset + i);
												setOpen(true);
											}}
										>
											<img
												src={gridSrc(it)}
												alt={gridAlt(it)}
												loading="lazy"
												className={`${baseClass}__img`}
												style={itemHeight ? { height: '100%', objectFit: 'cover' } : undefined}
												referrerPolicy="no-referrer"
											/>
											<span className={`${baseClass}__overlay`} />
										</button>
									))}
								</Masonry>
							</ResponsiveMasonry>
						);
					})}
				</div>
			) : (
				// ====== MODO MASONRY UNIFORME ======
				<ResponsiveMasonry columnsCountBreakPoints={columns}>
					<Masonry style={{ gap: gutter }} itemStyle={{ gap: gutter }}>
						{items.map((it, i) => (
							<button
								key={`${gridSrc(it)}-${i}`}
								type="button"
								aria-label={gridAlt(it) || 'Ver imagen'}
								className={`${baseClass}__item`}
								style={itemHeight ? { height: toPx(itemHeight) } : undefined}
								onClick={() => {
									setIndex(i);
									setOpen(true);
								}}
							>
								<img
									src={gridSrc(it)}
									alt={gridAlt(it)}
									loading="lazy"
									className={`${baseClass}__img`}
									style={itemHeight ? { height: '100%', objectFit: 'cover' } : undefined}
									referrerPolicy="no-referrer"
								/>
								<span className={`${baseClass}__overlay`} />
							</button>
						))}
					</Masonry>
				</ResponsiveMasonry>
			)}

			<Lightbox
				open={open}
				close={() => setOpen(false)}
				index={index}
				slides={slides}
				plugins={[Zoom, Captions]}
				controller={{ closeOnBackdropClick: true, closeOnPullDown: true }}
				carousel={{ padding: 16 }}
			/>
		</section>
	);
};
